/**
 * Planned work types + per-type quantities for pipeline sections (SOW).
 * API: `plannedWorkItems`: [{ workType, quantity }]; legacy `plannedWorks` / `plannedWelds` kept for roll-ups.
 */

export const PLANNED_WORK_TYPE_OPTIONS = [
  { value: 'Components', label: 'Components' },
  { value: 'Days', label: 'Days' },
  { value: 'Field Joints', label: 'Field Joints' },
  { value: 'Welds', label: 'Welds' },
  { value: 'Coating', label: 'Coating' },
  { value: 'Weld', label: 'Weld' },
];

const WELD_LIKE_FOR_ROLLUP = new Set(['Welds', 'Weld', 'Field Joints']);

function toNullableNumber(v) {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).trim());
  return Number.isFinite(n) ? n : null;
}

/** UI rows: { id, workType, quantity } */
export function parsePlannedWorkItemsFromRecord(record) {
  const raw = record?.plannedWorkItems ?? record?.planned_work_items;
  if (Array.isArray(raw) && raw.length) {
    return raw.map((it, idx) => ({
      id: `pwi-${idx}-${it.workType ?? it.work_type ?? idx}`,
      workType: String(it.workType ?? it.work_type ?? '').trim(),
      quantity:
        it.quantity != null && it.quantity !== '' ? String(it.quantity) : '',
    })).filter((x) => x.workType);
  }
  const legacyWork = record?.plannedWorks ?? record?.planned_works ?? '';
  const legacyQty = record?.plannedWelds ?? record?.planned_welds;
  if (legacyWork != null && String(legacyWork).trim() !== '') {
    return [
      {
        id: 'legacy',
        workType: String(legacyWork).trim(),
        quantity:
          legacyQty != null && legacyQty !== '' ? String(legacyQty) : '',
      },
    ];
  }
  return [];
}

export function formatPlannedWorkSummary(recordOrItems) {
  const items = Array.isArray(recordOrItems)
    ? recordOrItems
    : parsePlannedWorkItemsFromRecord(recordOrItems);
  if (!items.length) return '—';
  return items
    .map((it) => {
      const wt = it.workType ?? '';
      const q =
        it.quantity != null && String(it.quantity).trim() !== ''
          ? String(it.quantity).trim()
          : '—';
      return `${wt}: ${q}`;
    })
    .join('; ');
}

export function rollupWeldsQuantityFromItems(items) {
  let sum = 0;
  let any = false;
  (Array.isArray(items) ? items : []).forEach((it) => {
    const wt = it.workType ?? it.work_type;
    if (!WELD_LIKE_FOR_ROLLUP.has(wt)) return;
    const q = toNullableNumber(it.quantity ?? it.qty);
    if (q != null) {
      sum += q;
      any = true;
    }
  });
  return any ? sum : null;
}

/** Single-cell rollup for tables (weld-like quantities only). */
export function displayPlannedQtySummary(record) {
  const items = record?.plannedWorkItems ?? record?.planned_work_items;
  if (Array.isArray(items) && items.length > 0) {
    const rolled = rollupWeldsQuantityFromItems(items);
    if (rolled != null) return String(rolled);
    return '—';
  }
  if (record?.plannedWelds != null && record?.plannedWelds !== '')
    return String(record.plannedWelds);
  return '—';
}

/** Display unit per planned work type (pipeline header chips). */
export const PLANNED_WORK_DISPLAY_UNITS = {
  Components: 'ea',
  Days: 'days',
  'Field Joints': 'joints',
  Welds: 'welds',
  Weld: 'welds',
  Coating: 'm',
};

export function plannedWorkTypeUnit(workType) {
  const wt = String(workType || '').trim();
  return PLANNED_WORK_DISPLAY_UNITS[wt] || '';
}

export function getPlannedWeldsTotalForRecord(record) {
  const items = parsePlannedWorkItemsFromRecord(record);
  const rolled = rollupWeldsQuantityFromItems(items);
  if (rolled != null) return rolled;
  return toNullableNumber(record?.plannedWelds ?? record?.planned_welds);
}

/** Sum weld-like planned quantities across all pipeline (SOW) rows. */
export function sumPlannedWeldsAcrossRecords(records) {
  if (!Array.isArray(records)) return null;
  let sum = 0;
  let any = false;
  records.forEach((r) => {
    const q = getPlannedWeldsTotalForRecord(r);
    if (q != null) {
      sum += q;
      any = true;
    }
  });
  return any ? sum : null;
}

/** Per work-type quantity totals across pipelines (for summary chips). */
export function aggregatePlannedWorkQuantitiesByType(records) {
  if (!Array.isArray(records)) return [];
  const totals = new Map();
  records.forEach((r) => {
    parsePlannedWorkItemsFromRecord(r).forEach((it) => {
      const wt = String(it.workType || '').trim();
      if (!wt) return;
      const q = toNullableNumber(it.quantity);
      if (q == null) return;
      totals.set(wt, (totals.get(wt) || 0) + q);
    });
  });
  return [...totals.entries()]
    .map(([workType, quantity]) => ({ workType, quantity }))
    .sort((a, b) => a.workType.localeCompare(b.workType));
}

/** Fields to merge into add/update SOW payload. */
export function buildPlannedWorkApiFields(uiRows) {
  const cleaned = (Array.isArray(uiRows) ? uiRows : [])
    .filter((it) => it.workType && String(it.workType).trim())
    .map((it) => ({
      workType: String(it.workType).trim(),
      quantity: toNullableNumber(it.quantity),
    }));
  const rollup = rollupWeldsQuantityFromItems(cleaned);
  const legacyWorks = cleaned.map((c) => c.workType).filter(Boolean).join(', ');
  return {
    ...(cleaned.length ? { plannedWorkItems: cleaned } : {}),
    ...(legacyWorks ? { plannedWorks: legacyWorks } : {}),
    ...(rollup != null ? { plannedWelds: rollup } : {}),
  };
}
